import React from "react";
import {
  Modal,
  Form,
  Button,
  List,
  notification,
  Divider,
  Skeleton,
  Tag,
} from "antd";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import {
  fetchPunchListItemDetail,
  postPunchListItemResponse,
} from "../services/punchListsAPI";
import { formatDateTimeLocal } from "../utils/dateUtils";
import { sanitizeHtml } from "../utils/textUtils";
import AttachmentsView from "./AttachmentsView";
import AttachmentsSection from "./AttachmentsSection";
import SimpleRichTextEditor from "./SimpleRichTextEditor";

const ViewPunchListItemModal = ({
  visible,
  onClose,
  punchListId,
  punchListItemId,
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { data: detail, isLoading } = useQuery({
    queryKey: ["punch-list-item-detail", punchListId, punchListItemId],
    queryFn: () => fetchPunchListItemDetail(punchListId, punchListItemId),
    enabled: !!visible && !!punchListId && !!punchListItemId,
    staleTime: 0,
  });

  const mutation = useMutation({
    mutationFn: ({ body }) =>
      postPunchListItemResponse(punchListId, punchListItemId, body),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "punch-list-item-detail",
        punchListId,
        punchListItemId,
      ]);
      queryClient.invalidateQueries(["punch-list-items"]);
      notification.success({ message: "Response added successfully" });
      form.resetFields();
    },
    onError: (error) => {
      notification.error({
        message: "Failed to add response",
        description: error.message,
      });
    },
  });

  const handleAddResponse = (values) => {
    mutation.mutate({
      body: {
        response: values.response,
        attachments: values.attachments || [],
      },
    });
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="View Punch List Item"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={800}
    >
      {isLoading && !detail ? (
        <Skeleton active />
      ) : detail ? (
        <>
          <h2 className="mb-2">
            #{detail.number} — {detail.title}
          </h2>
          <p>
            <strong>Status:</strong>{" "}
            <Tag color={detail.status === "Complete" ? "default" : "orange"}>
              {detail.status}
            </Tag>
          </p>
          <p>
            <strong>Approver:</strong>{" "}
            {detail.approver?.username || detail.approver_id || "—"}
          </p>
          <p>
            <strong>Assignees:</strong>{" "}
            {(detail.assignees || [])
              .map((a) => a.username || a.email)
              .filter(Boolean)
              .join(", ") || "—"}
          </p>
          {detail.location ? (
            <p>
              <strong>Location:</strong> {detail.location}
            </p>
          ) : null}

          {detail.linked_drawings?.length > 0 && (
            <>
              <Divider />
              <div>
                <strong>Linked Drawings:</strong>
                <List
                  className="mt-2"
                  size="small"
                  bordered
                  dataSource={detail.linked_drawings}
                  renderItem={(drawing) => {
                    const label =
                      drawing.display_label ||
                      [
                        drawing.drawing_number
                          ? `Drawing ${drawing.drawing_number}`
                          : null,
                        drawing.title,
                        drawing.revision_title
                          ? `Rev.${drawing.revision_title}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(": ") ||
                      "Drawing";
                    const canOpen =
                      drawing.drawing_folder_id && drawing.drawing_id;
                    return (
                      <List.Item>
                        {canOpen ? (
                          <Link
                            to={`/drawings/${drawing.drawing_folder_id}/${drawing.drawing_id}`}
                          >
                            {label}
                          </Link>
                        ) : (
                          <span>{label}</span>
                        )}
                      </List.Item>
                    );
                  }}
                />
              </div>
            </>
          )}

          {detail.attachments?.length > 0 && (
            <>
              <Divider />
              <div>
                <strong>Item attachments:</strong>
                <div className="mt-2">
                  <AttachmentsView attachments={detail.attachments} canDelete={false} />
                </div>
              </div>
            </>
          )}

          <Divider className="mb-3" />
          <h3 className="mt-0 mb-3">Responses</h3>
          <List
            className="mt-0"
            bordered
            dataSource={detail.responses || []}
            locale={{ emptyText: "No responses yet" }}
            renderItem={(item) => (
              <List.Item>
                <div className="w-full">
                  <div className="mb-2">
                    <strong>{item.responded_by?.username || "User"}:</strong>{" "}
                    <div
                      className="mt-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(item.response),
                      }}
                    />
                  </div>
                  {item.attachments?.length > 0 && (
                    <div className="mt-2">
                      <AttachmentsView
                        attachments={item.attachments}
                        canDelete={false}
                      />
                    </div>
                  )}
                  <p className="text-gray-500 text-sm mt-2">
                    {formatDateTimeLocal(item.responded_on)}
                  </p>
                </div>
              </List.Item>
            )}
          />

          {detail.can_respond && projectId && (
            <Form
              form={form}
              onFinish={handleAddResponse}
              layout="vertical"
              className="mt-4"
            >
              <Form.Item
                label="Add response"
                name="response"
                rules={[
                  { required: true, message: "Please enter your response." },
                ]}
              >
                <SimpleRichTextEditor placeholder="Enter your response..." />
              </Form.Item>

              <Form.Item name="attachments" label="Attachments">
                <AttachmentsSection
                  projectId={projectId}
                  label="Add response attachments"
                  title="Upload response documents"
                  description="Upload documents for your response"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={mutation.isPending}
                >
                  Submit response
                </Button>
              </Form.Item>
            </Form>
          )}
        </>
      ) : (
        <Skeleton active />
      )}
    </Modal>
  );
};

export default ViewPunchListItemModal;
